import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  SelectChangeEvent,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Grid,
  Avatar,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  Zoom,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Sort as SortIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  DirectionsCar as CarIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { animated } from '@react-spring/web';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { Sale, PaymentMethod, PAYMENT_METHODS } from '../../types';
import saleService from '../../services/saleService';
import { isConsignmentCommissionFixed } from '../../utils/consignmentCommission';
import { getCashRecognizedProfitFromSale } from '../../utils/saleRealProfit';
import { useAuthUser } from '../../contexts/AuthUserContext';
import TermSalesReportPDF from './TermSalesReportPDF';
import { buildTermSalesReportItems } from './termSalesReportUtils';
import { exportTermSalesReportExcel } from '../../utils/reportExcelExports';

const AnimatedTableRow = animated(TableRow);
const AnimatedCard = animated(Card);

// Componente para ícone de método de pagamento
const PaymentMethodIcon = ({ method }: { method: PaymentMethod }) => {
  switch (method) {
    case 'money':
      return <MoneyIcon />;
    case 'pix':
      return <PaymentIcon />;
    case 'bank_transfer':
      return <PaymentIcon />;
    case 'check':
      return <PaymentIcon />;
    case 'financing':
      return <PaymentIcon />;
    case 'term':
      return <PaymentIcon color="secondary" />;
    default:
      return <PaymentIcon />;
  }
};

// No componente SaleList, adicionar um componente para o tooltip de lucro
const ProfitTooltip = ({ sale }: { sale: Sale }) => {
  const profit = getCashRecognizedProfitFromSale(sale);
  const purchasePrice = sale.vehicle?.purchase_price || 0;
  const totalExpenses = (sale.vehicle?.expenses || []).reduce((accum, expense) => {
    return accum + expense.amount;
  }, 0);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
        Detalhes do Lucro:
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Preço de Venda:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(sale.sale_price)}</Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">
          {sale.vehicle?.is_consignado ? 'Comissão:' : 'Preço de Venda:'}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {sale.vehicle?.is_consignado
            ? (isConsignmentCommissionFixed(sale.vehicle.consignment_commission_type)
              ? formatCurrency(Number(sale.vehicle.consignment_commission || 0))
              : `${Number(sale.vehicle.consignment_commission || 0)}%`)
            : formatCurrency(sale.sale_price)}
        </Typography>
      </Box>

      {!sale.vehicle?.is_consignado && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Preço de Compra:</Typography>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {formatCurrency(purchasePrice)}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Despesas:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(totalExpenses)}</Typography>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Lucro Total:</Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: profit >= 0 ? 'success.main' : 'error.main' }}>
          {formatCurrency(profit)}
        </Typography>
      </Box>
    </Box>
  );
};

const SaleList: React.FC = () => {
  const navigate = useNavigate();
  const { canWrite, isViewer } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'last30' | 'last90' | 'thisYear'>('all');
  const [vehicleFilter, setVehicleFilter] = useState<number | 'all'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'price'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSaleId, setSelectedSaleId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [downloadingTermPdf, setDownloadingTermPdf] = useState(false);
  const [downloadingTermExcel, setDownloadingTermExcel] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchSales = async () => {
      try {
        setLoading(true);

        // Usando o serviço real para buscar as vendas
        const data = await saleService.getAllSales();
        const enriched = await saleService.enrichTermSalesWithInstallments(data || []);
        setSales(enriched);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar vendas:', error);
        setError('Não foi possível carregar as vendas. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchSales();
  }, []);

  useEffect(() => {
    // Automatically switch to card view on mobile devices
    if (isMobile) {
      setViewMode('card');
    } else {
      setViewMode('table');
    }
  }, [isMobile]);

  const handleAddSale = () => {
    navigate('/vendas/novo');
  };

  const handleEditSale = (id: number) => {
    navigate(`/vendas/editar/${id}`);
  };

  const handleDeleteSale = async (id: number) => {
    const confirmed = window.confirm('Tem certeza que deseja excluir esta venda?');
    if (!confirmed) return;

    const sale = sales.find((item) => item.id === id);

    try {
      await saleService.deleteSale(id, sale?.vehicle_id);
      setSales((prev) => prev.filter((item) => item.id !== id));
      setError(null);
    } catch {
      setError('Não foi possível excluir a venda. Tente novamente.');
    }
  };

  const handleViewSale = (id: number) => {
    // Em uma aplicação real, redirecionaríamos para a página de detalhes
    navigate(`/vendas/${id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, saleId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedSaleId(saleId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSaleId(null);
  };

  const handleDateFilterChange = (event: SelectChangeEvent<string>) => {
    setDateFilter(event.target.value as 'all' | 'last30' | 'last90' | 'thisYear');
  };

  const handleVehicleFilterChange = (event: SelectChangeEvent<string>) => {
    setVehicleFilter(event.target.value as number | 'all');
  };

  const handlePaymentMethodFilterChange = (event: SelectChangeEvent<string>) => {
    setPaymentMethodFilter(event.target.value as PaymentMethod | 'all');
  };

  const handleSortByChange = (sortField: 'date' | 'price') => {
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('desc');
    }
  };

  // Filtrar vendas com base nos critérios de pesquisa
  const filteredSales = sales.filter((sale) => {
    // Filtro de texto
    const matchesSearch = (
      (sale.customer_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (sale.customer_contact?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (sale.vehicle?.brand?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (sale.vehicle?.model?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (sale.vehicle?.plate?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    // Filtro de método de pagamento
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.payment_method === paymentMethodFilter;

    // Filtro de veículo
    const matchesVehicle = vehicleFilter === 'all' || sale.vehicle_id === vehicleFilter;

    // Filtro de data
    let matchesDate = true;
    const today = new Date();
    const saleDate = new Date(sale.sale_date);

    if (dateFilter === 'last30') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      matchesDate = saleDate >= thirtyDaysAgo;
    } else if (dateFilter === 'last90') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(today.getDate() - 90);
      matchesDate = saleDate >= ninetyDaysAgo;
    } else if (dateFilter === 'thisYear') {
      matchesDate = saleDate.getFullYear() === today.getFullYear();
    }

    return matchesSearch && matchesPaymentMethod && matchesVehicle && matchesDate;
  });

  // Ordenar vendas
  const sortedSales = [...filteredSales].sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a.sale_date).getTime();
      const dateB = new Date(b.sale_date).getTime();
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortBy === 'price') {
      return sortDirection === 'asc' ? a.sale_price - b.sale_price : b.sale_price - a.sale_price;
    }
    return 0;
  });

  const termSalesInFilter = filteredSales.filter((sale) => sale.payment_method === 'term');

  // Total em reais das vendas (faturamento)
  const totalSalesAmount = filteredSales.reduce((sum, sale) => sum + sale.sale_price, 0);

  const showProfit = !isViewer;

  // Lucro reconhecido no caixa (vendas a prazo: proporcional às baixas)
  const totalProfit = showProfit
    ? filteredSales.reduce((sum, sale) => sum + getCashRecognizedProfitFromSale(sale), 0)
    : 0;

  // Para o tooltip: custo total (compra + despesas) só para vendas não consignadas
  const totalPurchasePriceWithExpenses = showProfit
    ? filteredSales.reduce((sum, sale) => {
        const vehicle = sale.vehicle;
        if (!vehicle) return sum;
        const totalExpenses = vehicle.expenses?.reduce((expenseSum, expense) => expenseSum + expense.amount, 0) || 0;
        if (vehicle.is_consignado) return sum + totalExpenses; // consignado: só despesas
        return sum + (vehicle.purchase_price || 0) + totalExpenses;
      }, 0)
    : 0;

  // Formatação de datas e valores
  const getTermPdfFilterDescription = () => {
    const parts: string[] = [];
    if (paymentMethodFilter === 'term') parts.push('pagamento: a prazo');
    if (dateFilter === 'last30') parts.push('período: últimos 30 dias');
    else if (dateFilter === 'last90') parts.push('período: últimos 90 dias');
    else if (dateFilter === 'thisYear') parts.push('período: este ano');
    if (vehicleFilter !== 'all') {
      const saleWithVehicle = sales.find((s) => s.vehicle_id === vehicleFilter);
      const v = saleWithVehicle?.vehicle;
      if (v) parts.push(`veículo: ${`${v.brand ?? ''} ${v.model ?? ''}`.trim()}`);
    }
    if (searchTerm.trim()) parts.push(`busca: "${searchTerm.trim()}"`);
    return parts.length ? `Filtros: ${parts.join(' · ')}` : 'Todas as vendas a prazo nos filtros atuais';
  };

  const handleDownloadTermSalesPdf = async () => {
    if (termSalesInFilter.length === 0) {
      window.alert('Nenhuma venda a prazo encontrada com os filtros atuais.');
      return;
    }

    setDownloadingTermPdf(true);
    try {
      const items = await buildTermSalesReportItems(termSalesInFilter);
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;

      const doc = (
        <TermSalesReportPDF
          items={items}
          generatedAtLabel={generatedAtLabel}
          logoUrl={logoUrl}
          filterDescription={getTermPdfFilterDescription()}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Vendas-a-prazo-${generatedAtLabel.replace(/[/:\\s]/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o PDF das vendas a prazo.');
    } finally {
      setDownloadingTermPdf(false);
    }
  };

  const handleDownloadTermSalesExcel = async () => {
    if (termSalesInFilter.length === 0) {
      window.alert('Nenhuma venda a prazo encontrada com os filtros atuais.');
      return;
    }

    setDownloadingTermExcel(true);
    try {
      const items = await buildTermSalesReportItems(termSalesInFilter);
      const generatedAtLabel = format(new Date(), 'dd/MM/yyyy HH:mm');
      await exportTermSalesReportExcel({
        items,
        generatedAtLabel,
        filterDescription: getTermPdfFilterDescription(),
      });
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o Excel das vendas a prazo.');
    } finally {
      setDownloadingTermExcel(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'; // Retorna um hífen se a data não existir

    try {


      const cleanDateString = dateString.split('T')[0];  // Vai pegar apenas '2025-05-01'
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-'; // Retorna um hífen em caso de erro
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Animação simples para linhas da tabela
  const rowAnimationStyle = {
    opacity: 1,
    transform: 'translateY(0)'
  };

  // Estilos de animação para cards
  const cardAnimationStyle = {
    opacity: 1,
    transform: 'scale(1)'
  };

  // Calcular vendas paginadas
  const paginatedSales = sortedSales.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Calcular número total de páginas
  const totalPages = Math.ceil(sortedSales.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  // Array de veículos únicos para o filtro
  const uniqueVehicles = Array.from(
    new Set(sales.filter(sale => sale.vehicle_id).map(sale => sale.vehicle_id))
  ).map(vehicleId => {
    const sale = sales.find(sale => sale.vehicle_id === vehicleId);
    const vehicle = sale?.vehicle;
    return vehicle ? {
      id: vehicle.id,
      name: `${vehicle.brand || ''} ${vehicle.model || ''} ${vehicle.plate ? `(${vehicle.plate})` : ''}`
    } : null;
  }).filter(Boolean) as { id: number; name: string }[];

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Vendas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gerenciamento de vendas de veículos
            </Typography>
          </Box>
          {canWrite && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSale}
              sx={{
                py: 1.2,
                px: 3,
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                }
              }}
            >
              Nova Venda
            </Button>
          )}
        </Box>

        {/* Painéis com estatísticas de vendas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <MoneyIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Total de Vendas
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(totalSalesAmount)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {filteredSales.length} veículos vendidos
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {showProfit ? (
              <Paper sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '12px',
                backgroundImage: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}>
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Lucro Total
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={totalProfit >= 0 ? theme.palette.success.main : theme.palette.error.main}
                >
                  {formatCurrency(totalProfit)}
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Lucro real (soma por venda):
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Venda direta: preço − custo − despesas. Consignado: comissão − despesas.
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Faturamento (vendas):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(totalSalesAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Custos (compra + despesas):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(totalPurchasePriceWithExpenses)}</Typography>
                      </Box>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Lucro total:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: totalProfit >= 0 ? 'success.main' : 'error.main' }}>
                          {formatCurrency(totalProfit)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.98)',
                        color: theme.palette.text.primary,
                        boxShadow: '0 2px 14px rgba(0, 0, 0, 0.3)',
                        borderRadius: '8px',
                        p: 0,
                        maxWidth: 'none'
                      }
                    }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'help',
                    mt: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Margem de {totalSalesAmount > 0 ? Math.round((totalProfit / totalSalesAmount) * 100) : 0}%
                    </Typography>
                    <InfoIcon sx={{ fontSize: 16, opacity: 0.7, color: theme.palette.info.main }} />
                  </Box>
                </Tooltip>
              </Paper>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <CheckCircleIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Ticket Médio
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(filteredSales.length > 0 ? totalSalesAmount / filteredSales.length : 0)}
              </Typography>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                      Detalhes do Cálculo:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Total de Vendas:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(totalSalesAmount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Número de Veículos:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{filteredSales.length}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Ticket Médio:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(filteredSales.length > 0 ? totalSalesAmount / filteredSales.length : 0)}
                      </Typography>
                    </Box>
                  </Box>
                }
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.98)',
                      color: theme.palette.text.primary,
                      boxShadow: '0 2px 14px rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      p: 0,
                      maxWidth: 'none'
                    }
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'help',
                  mt: 1
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Por veículo vendido
                  </Typography>
                  <InfoIcon sx={{ fontSize: 16, opacity: 0.7, color: theme.palette.info.main }} />
                </Box>
              </Tooltip>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtros e pesquisa */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por cliente, veículo ou placa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.2)'
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}>
                <InputLabel id="payment-method-filter-label">Pagamento</InputLabel>
                <Select
                  labelId="payment-method-filter-label"
                  id="payment-method-filter"
                  value={paymentMethodFilter}
                  label="Pagamento"
                  onChange={handlePaymentMethodFilterChange}
                >
                  <MenuItem value="all">Todas as formas</MenuItem>
                  {Object.entries(PAYMENT_METHODS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentMethodIcon method={value as PaymentMethod} />
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.2)'
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}>
                <InputLabel id="vehicle-filter-label">Veículo</InputLabel>
                <Select
                  labelId="vehicle-filter-label"
                  id="vehicle-filter"
                  value={vehicleFilter === 'all' ? 'all' : vehicleFilter.toString()}
                  label="Veículo"
                  onChange={handleVehicleFilterChange}
                >
                  <MenuItem value="all">Todos Veículos</MenuItem>
                  {uniqueVehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                }
              }}>
                <InputLabel id="date-filter-label">Período</InputLabel>
                <Select
                  labelId="date-filter-label"
                  id="date-filter"
                  value={dateFilter}
                  label="Período"
                  onChange={handleDateFilterChange}
                >
                  <MenuItem value="all">Todas datas</MenuItem>
                  <MenuItem value="last30">Últimos 30 dias</MenuItem>
                  <MenuItem value="last90">Últimos 90 dias</MenuItem>
                  <MenuItem value="thisYear">Este ano</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  startIcon={downloadingTermExcel ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                  variant="outlined"
                  color="success"
                  size="small"
                  disabled={downloadingTermExcel || termSalesInFilter.length === 0}
                  onClick={() => void handleDownloadTermSalesExcel()}
                  sx={{ borderRadius: '8px' }}
                >
                  Excel a prazo ({termSalesInFilter.length})
                </Button>
                <Button
                  startIcon={downloadingTermPdf ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                  variant="outlined"
                  size="small"
                  disabled={downloadingTermPdf || termSalesInFilter.length === 0}
                  onClick={() => void handleDownloadTermSalesPdf()}
                  sx={{ borderRadius: '8px' }}
                >
                  PDF a prazo ({termSalesInFilter.length})
                </Button>
                <Button
                  startIcon={<SortIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const nextSort = sortBy === 'date' ? 'price' : 'date';
                    handleSortByChange(nextSort);
                  }}
                  sx={{ borderRadius: '8px' }}
                >
                  {sortBy === 'date' ? 'Por Data' : 'Por Valor'}
                  {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {filteredSales.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '12px' }}>
            <MoneyIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhuma venda encontrada</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '500px', mx: 'auto' }}>
              Não foram encontradas vendas com os filtros atuais. Tente modificar sua busca ou registre uma nova venda.
            </Typography>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSale}
                sx={{
                  py: 1.2,
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                }}
              >
                Nova Venda
              </Button>
            )}
          </Paper>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                mb: 3
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }}>
                    <TableCell>Data</TableCell>
                    <TableCell>Veículo</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Método de Pagamento</TableCell>
                    <TableCell align="right">Preço de Venda</TableCell>
                    {showProfit && <TableCell align="right">Lucro</TableCell>}
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSales.map((sale, index) => {
                    const profit = getCashRecognizedProfitFromSale(sale);

                    return (
                      <AnimatedTableRow
                        key={sale.id}
                        hover
                        style={{
                          ...rowAnimationStyle,
                          transition: `all 0.3s ease ${index * 0.1}s`
                        }}
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(63, 136, 197, 0.04)',
                            cursor: 'pointer',
                          }
                        }}
                        onClick={() => handleViewSale(sale.id)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DateIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                            {formatDate(sale.sale_date)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                            <Typography>
                              {sale.vehicle ?
                                `${sale.vehicle.brand} ${sale.vehicle.model} (${sale.vehicle.plate})` :
                                'Veículo não encontrado'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                              <Typography>{sale.buyer_name}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                              <Typography variant="body2" color="text.secondary">
                                {sale.buyer_phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<PaymentMethodIcon method={sale.payment_method} />}
                            label={PAYMENT_METHODS[sale.payment_method]}
                            sx={{
                              bgcolor: 'rgba(63, 136, 197, 0.1)',
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                              '& .MuiChip-icon': {
                                color: theme.palette.primary.main,
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(sale.sale_price)}
                        </TableCell>
                        {showProfit && (
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 'bold',
                              color: profit >= 0 ? theme.palette.success.main : theme.palette.error.main
                            }}
                          >
                            <Tooltip
                              title={<ProfitTooltip sale={sale} />}
                              arrow
                              placement="left"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.98)',
                                    color: theme.palette.text.primary,
                                    boxShadow: '0 2px 14px rgba(0, 0, 0, 0.3)',
                                    borderRadius: '8px',
                                    p: 0,
                                    maxWidth: 'none'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                {formatCurrency(profit)}
                                <InfoIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.7, color: theme.palette.info.main }} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="Mais opções" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, sale.id)}
                                sx={{
                                  transition: 'all 0.2s',
                                  '&:hover': { transform: 'rotate(90deg)' }
                                }}
                              >
                                <MoreIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </AnimatedTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedSales.map((sale, index) => {
                const profit = getCashRecognizedProfitFromSale(sale);

                return (
                  <Grid item xs={12} sm={6} key={sale.id}>
                    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Box>
                        <AnimatedCard
                          style={{
                            ...cardAnimationStyle,
                            transition: `all 0.3s ease ${index * 0.1}s`
                          }}
                          sx={{
                            borderRadius: '16px',
                            overflow: 'hidden',
                            transition: 'all 0.3s',
                            cursor: 'pointer',
                            position: 'relative',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                            }
                          }}
                          onClick={() => handleViewSale(sale.id)}
                        >
                          <Box
                            sx={{
                              height: '100px',
                              background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, #1d3a5a 0%, #121212 100%)'
                                : 'linear-gradient(135deg, #2c5282 0%, #1A365D 100%)',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              position: 'relative',
                              p: 2,
                            }}
                          >
                            <CarIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.8)', mb: 1 }} />
                            <Chip
                              label={formatCurrency(sale.sale_price)}
                              color="primary"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontWeight: 'bold',
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                              }}
                            />
                          </Box>

                          <CardContent>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {sale.vehicle ?
                                `${sale.vehicle.brand} ${sale.vehicle.model}` :
                                'Veículo não encontrado'}
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <DateIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    Data: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                      {formatDate(sale.sale_date).split(' de ')[0]}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <PaymentIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    Pagamento: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                      {PAYMENT_METHODS[sale.payment_method]}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PersonIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                              <Typography variant="body2" color="text.secondary">
                                Cliente: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                                  {sale.customer_name}
                                </Box>
                              </Typography>
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                          {showProfit && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Lucro:
                              </Typography>
                              <Tooltip
                                title={<ProfitTooltip sale={sale} />}
                                arrow
                                placement="left"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.98)',
                                      color: theme.palette.text.primary,
                                      boxShadow: '0 2px 14px rgba(0, 0, 0, 0.3)',
                                      borderRadius: '8px',
                                      p: 0,
                                      maxWidth: 'none'
                                    }
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 'bold',
                                      color: profit >= 0 ? theme.palette.success.main : theme.palette.error.main
                                    }}
                                  >
                                    {formatCurrency(profit)}
                                  </Typography>
                                  <InfoIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.7, color: theme.palette.info.main }} />
                                </Box>
                              </Tooltip>
                            </Box>
                          )}
                          </CardContent>

                          <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                            <Box onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, sale.id)}
                              >
                                <MoreIcon />
                              </IconButton>
                            </Box>
                          </CardActions>
                        </AnimatedCard>
                      </Box>
                    </Zoom>
                  </Grid>
                );
              })}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        )}

        {/* Menu de ações */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <MenuItem onClick={() => {
            if (selectedSaleId) handleViewSale(selectedSaleId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver Detalhes</ListItemText>
          </MenuItem>
          {canWrite && (
            <>
              <MenuItem onClick={() => {
                if (selectedSaleId) handleEditSale(selectedSaleId);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Editar</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => {
                if (selectedSaleId) handleDeleteSale(selectedSaleId);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText sx={{ color: theme.palette.error.main }}>Excluir</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    </Fade>
  );
};

export default SaleList; 